/**
 * /profile — the player's own account page.
 *
 * VENUE-AGNOSTIC, which is why it moved off /venues/:venueSlug/profile. An
 * account belongs to a person, not to a venue: a player with no current venue
 * still needs somewhere to land, and post-signup that is here. The venue-shaped
 * things that belong on this page — favouriting a venue, per-venue
 * notifications — become SECTIONS of it when they are built, not the reason for
 * its URL.
 *
 * ---------------------------------------------------------------------------
 * FOUR BLOCKS, NO CARDS
 *
 * Profile, Notifications, Privacy, Account. They sit directly on the page
 * background, separated by their headings and the space around them. A settings
 * page built from bordered panels reads as four unrelated widgets rather than
 * one account. Two borders are drawn deliberately: a rule between settings
 * rows, and one around the delete panel, which is the only control here that
 * cannot be undone.
 *
 * ---------------------------------------------------------------------------
 * WHEN THIS PAGE RE-READS THE SESSION, AND WHEN IT DOES NOT
 *
 * PUT /api/auth/me answers with the WHOLE session user, so those writes hand
 * the object straight to applyUser and cost one request. Everything else calls
 * refresh(), a token reissue plus a /me: the avatar routes answer with a URL
 * and change-email answers with a status, and neither is a user.
 *
 * Both go through AuthContext. Nothing here keeps its own copy of the session,
 * because two copies is how a sidebar ends up showing a name this form has
 * already changed.
 *
 * ---------------------------------------------------------------------------
 * THE BANNER DOES NOT REGISTER ANYBODY, AND THAT IS THE POINT
 *
 * Somebody who tapped Register on a tournament, was asked to make an account,
 * and made one has NOT entered that tournament. Signing up and entering are two
 * different acts, and quietly completing the second because they did the first
 * would put a player in a draw they never confirmed — against a fee, a cap and
 * a check-in they have not seen. So the banner says so plainly and links back.
 *
 * The label is re-read rather than carried in the URL: a label taken from a
 * query param is arbitrary text an arbitrary link could put in a banner on this
 * player's own profile. Two public GETs, because the tournament route is keyed
 * on a numeric venue id while the URL carries a slug — and both only run when
 * there is a banner to draw at all.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiGet, apiPost, apiPut, apiDel, apiUpload } from '../lib/api.js';
import { useAuth } from '../context/AuthContext.jsx';
import { readSignupReturn } from '../lib/signupReturn.js';

/** Where a data request goes. One address, in one place, until there is a route. */
const DATA_REQUEST_EMAIL = 'thehopdarts@gmail.com';

/**
 * What the file picker offers, matching ALLOWED_TYPES in functions/lib/media.js
 * exactly. Offering a format the API refuses spends a round trip learning
 * something the picker already knew.
 */
const ACCEPT_IMAGES = 'image/jpeg,image/png,image/webp';

/** The stand-in for an account with no photo. Never stored on the row. */
const DEFAULT_AVATAR = '/default-avatar.jpg';

/**
 * How the rest of the app names a night. Tournaments have no name column — see
 * schema.sql — so this is the convention AdminTournaments, Pair and
 * PublicTournaments all already use, with the venue added because this page is
 * not scoped to one.
 */
function tournamentLabel(tournament, venueName) {
  const night = tournament?.night_number != null
    ? `Night ${tournament.night_number}`
    : `#${tournament?.id}`;

  return venueName ? `${night} at ${venueName}` : night;
}

/**
 * One labelled switch.
 *
 * A button with role="switch" rather than a checkbox: it reports its own state
 * to assistive tech, takes the design system's colours without fighting a
 * native control's, and disables cleanly while a save is in flight.
 */
function Toggle({ label, hint, checked, busy, onChange }) {
  return (
    <div className="hd-setting">
      <div className="hd-setting-text">
        <span>{label}</span>
        {hint && <span className="hd-hint">{hint}</span>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        className="hd-switch"
        disabled={busy}
        onClick={() => onChange(!checked)}
      >
        <span className="hd-switch-knob" />
      </button>
    </div>
  );
}

/** An inline result line under one form. Nothing to say renders nothing. */
function Note({ result }) {
  if (!result) return null;

  return (
    <p
      className="hd-hint"
      role={result.ok ? 'status' : 'alert'}
      style={{ marginBottom: 0, color: result.ok ? 'var(--hd-success)' : 'var(--hd-danger)' }}
    >
      {result.message}
    </p>
  );
}

export default function Profile() {
  const [params] = useSearchParams();
  const signupReturn = useMemo(() => readSignupReturn(params), [params]);

  const { user, applyUser, refresh, logout } = useAuth();
  const navigate = useNavigate();

  const [resolved, setResolved] = useState(null);

  useEffect(() => {
    if (!signupReturn) { setResolved(null); return undefined; }

    let cancelled = false;

    (async () => {
      const venue = await apiGet(`/api/venues/by-slug/${signupReturn.venueSlug}`);
      if (cancelled || !venue.data?.venue) return;

      const { data } = await apiGet(
        `/api/venues/${venue.data.venue.id}/tournaments/${signupReturn.tournamentId}`
      );
      if (cancelled || !data?.tournament) return;

      setResolved({
        label:  tournamentLabel(data.tournament, venue.data.venue.name),
        status: data.tournament.status,
      });
    })();

    return () => { cancelled = true; };
  }, [signupReturn]);

  const backTo = signupReturn
    ? `/venues/${signupReturn.venueSlug}/tournaments/${signupReturn.tournamentId}/register`
    : null;

  // ---- editable fields ----------------------------------------------------

  const [name,     setName]     = useState('');
  const [nickname, setNickname] = useState('');
  const [phone,    setPhone]    = useState('');

  // Seeded from the session when it lands, and re-seeded whenever the session's
  // own copy changes. Typing is local until Save, which is why the inputs do
  // not read straight from `user`.
  useEffect(() => {
    if (!user) return;
    setName(user.name ?? '');
    setNickname(user.nickname ?? '');
    setPhone(user.phone ?? '');
  }, [user?.name, user?.nickname, user?.phone]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- block 1: avatar, name, nickname ------------------------------------

  const fileRef = useRef(null);

  const [profileBusy,   setProfileBusy]   = useState(false);
  const [profileResult, setProfileResult] = useState(null);
  const [avatarBusy,    setAvatarBusy]    = useState(false);
  const [avatarResult,  setAvatarResult]  = useState(null);

  async function saveProfile(event) {
    event.preventDefault();

    if (!name.trim()) {
      setProfileResult({ ok: false, message: 'Display name cannot be empty' });
      return;
    }

    setProfileBusy(true);
    const { data, error } = await apiPut('/api/auth/me', {
      name:     name.trim(),
      nickname: nickname.trim(),
    });
    setProfileBusy(false);

    if (error) { setProfileResult({ ok: false, message: error }); return; }

    applyUser(data.user);
    setProfileResult({ ok: true, message: 'Saved' });
  }

  async function uploadAvatar(event) {
    const file = event.target.files?.[0];

    // Clearing the input matters: picking the SAME file twice fires no change
    // event otherwise, so a failed upload could not be retried.
    event.target.value = '';
    if (!file) return;

    setAvatarResult(null);
    setAvatarBusy(true);

    const form = new FormData();
    form.append('file', file);

    const { error } = await apiUpload('/api/auth/avatar', form);

    if (error) {
      setAvatarBusy(false);
      setAvatarResult({ ok: false, message: error });
      return;
    }

    // The avatar routes answer with a URL, not a user, so this one re-reads.
    await refresh();
    setAvatarBusy(false);
  }

  async function removeAvatar() {
    setAvatarResult(null);
    setAvatarBusy(true);

    const { error } = await apiDel('/api/auth/avatar');

    if (error) {
      setAvatarBusy(false);
      setAvatarResult({ ok: false, message: error });
      return;
    }

    await refresh();
    setAvatarBusy(false);
  }

  // ---- blocks 2 and 3: the switches ---------------------------------------

  const [flagBusy,   setFlagBusy]   = useState(null);
  const [flagResult, setFlagResult] = useState(null);

  /**
   * One switch write.
   *
   * `confirmOff` is asked only on the way OFF. Turning notifications back on
   * warrants no warning, and confirming every click trains people to dismiss
   * the dialog without reading it.
   */
  async function setFlag(field, value, confirmOff) {
    if (!value && confirmOff && !window.confirm(confirmOff)) return;

    setFlagResult(null);
    setFlagBusy(field);

    const { data, error } = await apiPut('/api/auth/me', { [field]: value ? 1 : 0 });
    setFlagBusy(null);

    if (error) { setFlagResult({ ok: false, message: error }); return; }

    applyUser(data.user);
  }

  // ---- block 4: account ----------------------------------------------------

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword,     setNewPassword]     = useState('');
  const [passwordBusy,    setPasswordBusy]    = useState(false);
  const [passwordResult,  setPasswordResult]  = useState(null);

  async function changePassword(event) {
    event.preventDefault();

    setPasswordBusy(true);
    const { error } = await apiPost('/api/auth/change-password', {
      currentPassword,
      newPassword,
    });
    setPasswordBusy(false);

    if (error) { setPasswordResult({ ok: false, message: error }); return; }

    setCurrentPassword('');
    setNewPassword('');
    setPasswordResult({ ok: true, message: 'Password changed' });
  }

  const [emailPassword, setEmailPassword] = useState('');
  const [newEmail,      setNewEmail]      = useState('');
  const [emailBusy,     setEmailBusy]     = useState(false);
  const [emailResult,   setEmailResult]   = useState(null);

  async function changeEmail(event) {
    event.preventDefault();

    setEmailBusy(true);
    const { error } = await apiPost('/api/auth/change-email', {
      password: emailPassword,
      newEmail,
    });

    if (error) {
      setEmailBusy(false);
      setEmailResult({ ok: false, message: error });
      return;
    }

    setEmailPassword('');
    setNewEmail('');

    // pendingEmail lives on the session user and this endpoint answers with a
    // status, so the note above this form needs a re-read to appear.
    await refresh();
    setEmailBusy(false);
    setEmailResult({ ok: true, message: 'Check the new address for a confirmation link' });
  }

  const [phoneBusy,   setPhoneBusy]   = useState(false);
  const [phoneResult, setPhoneResult] = useState(null);

  async function savePhone(event) {
    event.preventDefault();

    setPhoneBusy(true);
    const { data, error } = await apiPut('/api/auth/me', { phone: phone.trim() });
    setPhoneBusy(false);

    if (error) { setPhoneResult({ ok: false, message: error }); return; }

    applyUser(data.user);
    setPhoneResult({ ok: true, message: 'Saved' });
  }

  const [deleteOpen,     setDeleteOpen]     = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteBusy,     setDeleteBusy]     = useState(false);
  const [deleteResult,   setDeleteResult]   = useState(null);

  async function deleteAccount(event) {
    event.preventDefault();

    setDeleteBusy(true);
    const { error } = await apiPost('/api/auth/delete-account', {
      password: deletePassword,
    });

    if (error) {
      setDeleteBusy(false);
      setDeleteResult({ ok: false, message: error });
      return;
    }

    // The session is already dead server-side — requireAuth refuses an inactive
    // account on the very next request — so this is about clearing the client.
    // logout() drops local state whatever the network says.
    await logout();
    navigate('/', { replace: true });
  }

  // The shell renders this route behind a guard, so a null user here is the
  // moment before the session lands rather than a signed-out visitor.
  if (!user) return null;

  const hasNickname = Boolean(user.nickname?.trim());

  return (
    <div className="hd-stack">
      {signupReturn && (
        <div className="hd-card" role="status">
          <strong>
            You haven&apos;t registered for {resolved?.label ?? 'that tournament'} yet
          </strong>
          <p className="hd-hint" style={{ marginBottom: 0 }}>
            Your account is ready — but creating it didn&apos;t enter you into the
            night. <Link to={backTo}>Register now</Link>.
          </p>
        </div>
      )}

      {/* ---- block 1: profile ---- */}
      <section className="hd-settings-block">
        <h1 className="hd-page-title">Profile</h1>

        <div className="hd-avatar-row">
          <button
            type="button"
            className="hd-avatar-btn"
            onClick={() => fileRef.current?.click()}
            disabled={avatarBusy}
            aria-label="Change your photo"
          >
            <img
              src={user.profilePicUrl ?? DEFAULT_AVATAR}
              alt=""
              className="hd-avatar"
            />
          </button>

          <div className="hd-setting-text">
            <span className="hd-hint">JPEG, PNG or WebP. Click the photo to change it.</span>

            {user.profilePicUrl && (
              <div>
                <button
                  type="button"
                  className="hd-btn hd-btn-quiet hd-btn-sm"
                  onClick={removeAvatar}
                  disabled={avatarBusy}
                >
                  Remove photo
                </button>
              </div>
            )}

            <Note result={avatarResult} />
          </div>

          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT_IMAGES}
            onChange={uploadAvatar}
            className="hd-visually-hidden"
          />
        </div>

        <form className="hd-stack" onSubmit={saveProfile}>
          <div className="hd-field">
            <label className="hd-label" htmlFor="hd-name">Display name</label>
            <input
              id="hd-name"
              className="hd-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              required
            />
          </div>

          <div className="hd-field">
            <label className="hd-label" htmlFor="hd-nickname">Nickname</label>
            <input
              id="hd-nickname"
              className="hd-input"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              maxLength={60}
              placeholder="Optional"
            />
          </div>

          <div className="hd-row">
            <button type="submit" className="hd-btn hd-btn-primary" disabled={profileBusy}>
              Save
            </button>
            <Note result={profileResult} />
          </div>
        </form>
      </section>

      {/* ---- block 2: notifications ---- */}
      <section className="hd-settings-block">
        <h2 className="hd-section-title">Notifications</h2>

        <div>
          <Toggle
            label="Email notifications"
            hint="Tournament reminders, waitlist offers and results."
            checked={user.emailNotifications}
            busy={flagBusy === 'email_notifications'}
            onChange={(value) => setFlag(
              'email_notifications',
              value,
              'This will silence all email notifications regardless of venue — are you sure?'
            )}
          />

          <Toggle
            label="Push notifications"
            hint="Board calls and announcements, on every device you have enabled."
            checked={user.pushNotifications}
            busy={flagBusy === 'push_notifications'}
            onChange={(value) => setFlag(
              'push_notifications',
              value,
              'This will silence all push notifications regardless of venue — are you sure?'
            )}
          />
        </div>

        <Note result={flagResult} />
      </section>

      {/* ---- block 3: privacy ---- */}
      <section className="hd-settings-block">
        <h2 className="hd-section-title">Privacy</h2>

        <div>
          <Toggle
            label="Hide my profile"
            hint="Keeps you off public profile and listing pages. Past results stay in the standings."
            checked={user.profileHidden}
            busy={flagBusy === 'profile_hidden'}
            onChange={(value) => setFlag('profile_hidden', value)}
          />

          {/* Only where there is a nickname to govern. A switch controlling
              something that does not exist is a question nobody can answer. */}
          {hasNickname && (
            <Toggle
              label="Show my nickname publicly"
              hint={`Boards and standings will call you ${user.nickname}.`}
              checked={user.showNicknamePublic}
              busy={flagBusy === 'show_nickname_public'}
              onChange={(value) => setFlag('show_nickname_public', value)}
            />
          )}
        </div>
      </section>

      {/* ---- block 4: account ---- */}
      <section className="hd-settings-block">
        <h2 className="hd-section-title">Account</h2>

        <form className="hd-stack" onSubmit={changePassword}>
          <h3 className="hd-label">Change password</h3>

          <div className="hd-field">
            <label className="hd-label" htmlFor="hd-current-password">Current password</label>
            <input
              id="hd-current-password"
              type="password"
              className="hd-input"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>

          <div className="hd-field">
            <label className="hd-label" htmlFor="hd-new-password">New password</label>
            <input
              id="hd-new-password"
              type="password"
              className="hd-input"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
            <span className="hd-hint">At least 8 characters.</span>
          </div>

          <div className="hd-row">
            <button type="submit" className="hd-btn hd-btn-secondary" disabled={passwordBusy}>
              Change password
            </button>
            <Note result={passwordResult} />
          </div>
        </form>

        <form className="hd-stack" onSubmit={changeEmail}>
          <h3 className="hd-label">Change email</h3>
          <p className="hd-hint" style={{ marginBottom: 0 }}>Currently {user.email}</p>

          {user.pendingEmail && (
            <p className="hd-hint" role="status" style={{ marginBottom: 0 }}>
              Verification email sent to <strong>{user.pendingEmail}</strong> —
              check your inbox to confirm the change. Your current address stays
              active until then.
            </p>
          )}

          <div className="hd-field">
            <label className="hd-label" htmlFor="hd-email-password">Your password</label>
            <input
              id="hd-email-password"
              type="password"
              className="hd-input"
              value={emailPassword}
              onChange={(e) => setEmailPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>

          <div className="hd-field">
            <label className="hd-label" htmlFor="hd-new-email">New email address</label>
            <input
              id="hd-new-email"
              type="email"
              className="hd-input"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              required
            />
          </div>

          <div className="hd-row">
            <button type="submit" className="hd-btn hd-btn-secondary" disabled={emailBusy}>
              Change email
            </button>
            <Note result={emailResult} />
          </div>
        </form>

        <form className="hd-stack" onSubmit={savePhone}>
          <h3 className="hd-label">Phone</h3>

          <div className="hd-field">
            <label className="hd-label" htmlFor="hd-phone">Phone number</label>
            <input
              id="hd-phone"
              type="tel"
              className="hd-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              maxLength={40}
              placeholder="Optional"
            />
          </div>

          <div className="hd-row">
            <button type="submit" className="hd-btn hd-btn-secondary" disabled={phoneBusy}>
              Save
            </button>
            <Note result={phoneResult} />
          </div>
        </form>

        <div>
          <h3 className="hd-label">Your data</h3>
          <p className="hd-hint" style={{ marginBottom: 0 }}>
            <a href={`mailto:${DATA_REQUEST_EMAIL}?subject=Data%20request`}>
              Request a copy of my data
            </a>
          </p>
        </div>

        <div>
          <h3 className="hd-label">Delete account</h3>

          {!deleteOpen ? (
            <button
              type="button"
              className="hd-btn hd-btn-danger"
              onClick={() => { setDeleteOpen(true); setDeleteResult(null); }}
            >
              Delete my account
            </button>
          ) : (
            /* Inline rather than a window.prompt: a prompt shows the password in
               clear text, cannot be styled, and leaves nowhere to say what
               deleting does and does not remove. */
            <form className="hd-danger-panel" onSubmit={deleteAccount}>
              <strong>Delete your account?</strong>

              <p className="hd-hint" style={{ marginBottom: 0 }}>
                You will be signed out and will not be able to sign back in.
                Your past results stay in the standings and the hall of fame,
                still under your name — a night you played is part of everybody
                else&apos;s results too, and taking it out would change theirs.
              </p>

              <div className="hd-field">
                <label className="hd-label" htmlFor="hd-delete-password">
                  Confirm your password
                </label>
                <input
                  id="hd-delete-password"
                  type="password"
                  className="hd-input"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>

              <div className="hd-row">
                <button type="submit" className="hd-btn hd-btn-danger" disabled={deleteBusy}>
                  Delete my account
                </button>
                <button
                  type="button"
                  className="hd-btn hd-btn-quiet"
                  onClick={() => { setDeleteOpen(false); setDeletePassword(''); }}
                  disabled={deleteBusy}
                >
                  Cancel
                </button>
              </div>

              <Note result={deleteResult} />
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
